import { NO_INTERPOLATION_ESCAPE } from "@src/raya-lucaria/i18n/interpolation";
import { Row } from "@tanstack/react-table";
import "@types";
import classNames from "classnames";

import { useTableContext } from "../context";
import { GRID_CELL_FOCUS_CLASSES } from "../grid-classes";
import Select from "../select";
import { resolveRowLabel } from "../utils";
import TableRowCell from "./cell";
import { useRowActivation } from "./use-row-activation";

interface TableRowsProps<T> {
  rows: Row<T>[];
  selectable: boolean;
}

function TableRows<T>({ rows, selectable }: TableRowsProps<T>) {
  const { table, editMode, t, selectionIntent } = useTableContext();
  const { activateRow, toggleSelection } = useRowActivation({
    table,
    selectable,
    editMode: !!editMode,
    selectionIntent,
  });

  const handleRowClick = (row: Row<T>, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const isInteractiveElement = target.closest(
      'a, button, input, select, textarea, label, [role="button"], [type="checkbox"]',
    );

    if (!isInteractiveElement) {
      activateRow(row, event.shiftKey);
    }
  };

  const handleCheckboxChange = (
    row: Row<T>,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isShiftKey = (event.nativeEvent as MouseEvent).shiftKey;
    toggleSelection(row, isShiftKey);
  };

  return (
    <tbody className="">
      {rows.map((row) => {
        const isSelected = row.getIsSelected();

        return (
          <tr
            key={row.id}
            data-row-id={row.id}
            aria-selected={selectable ? isSelected : undefined}
            onClick={
              editMode ? undefined : (event) => handleRowClick(row, event)
            }
            className={classNames({
              "cursor-pointer hover:bg-tint transition-all duration-200 ease-in-out select-none border-b border-b-divider last:border-b-0":
                selectable && !editMode,
              "bg-tint hover:bg-tint": isSelected && selectable,
            })}
          >
            {selectable && (
              <td
                className={classNames(
                  "overflow-hidden py-2 px-[13px] w-target",
                  GRID_CELL_FOCUS_CLASSES,
                )}
              >
                <Select
                  row
                  label={t("selectRow", {
                    row: resolveRowLabel(t, table, row),
                    interpolation: NO_INTERPOLATION_ESCAPE,
                  })}
                  checked={row.getIsSelected()}
                  onChange={(event) => handleCheckboxChange(row, event)}
                />
              </td>
            )}
            {row.getVisibleCells().map((cell) => (
              <TableRowCell key={cell.id} row={row} cell={cell} />
            ))}
          </tr>
        );
      })}
    </tbody>
  );
}

export default TableRows;
